import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, Boxes, Building2, Check, ChevronDown, ChevronRight, Copy, Download, FlaskConical, LayoutGrid, List, MapPin, Package, Plus, Printer,
  ScanLine, SlidersHorizontal, Workflow, X,
} from 'lucide-react';
import { useXomicsAuth } from './AuthContext';
import { generateCollections, loadCollection, loadCollectionCatalogue, loadCollectionPrinters, loadCollections, printCollectionLabels } from './api';
import CollectionOverview, {
  ActivatorAvatar, ChannelMark, ShortPlace, activatorName, activatorNote, placeLabel, stepLabel,
} from './CollectionActivity';
import { Empty, Failed, Head, Loading, Pill, SearchField, Timeline, WorkspaceTabs, XomicsSelect } from './parts';
import { Flag, FilterMenu, RangeTabs, ago, counted, useNow, when } from './MobileParts';
import { SpecimenGlyph } from './LaboratoryGlyphs';
import { departmentShort, ownSampleTypeName, workflowName } from './LaboratoryText';
import { Field, errorMessage, fieldAria } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './collections.css';

const text = (key, values) => tx(`ui.collections.${key}`, values);

function PipelineLabel({ pipeline }) {
  return <span className="xo-collection-pipeline" title={`${pipeline.name} · ${pipeline.mode} · v${pipeline.codeVersion}`}>
    <strong>{pipeline.displayName}</strong><small>v{pipeline.codeVersion}</small>
  </span>;
}

function WorkflowLinks({ pipelines, compact = false }) {
  const links = [...new Map(pipelines.flatMap(p => p.workflows).map(w => [w.versionId, w])).values()];
  if (pipelines.length === 0) return null;
  return <div className={`xo-collection-workflows${compact ? ' is-compact' : ''}`} title={tx('ui.collections.workflow_links_hint')}>
    {!compact && <span><Workflow aria-hidden="true" />{tx('ui.collections.workflow_links')}</span>}
    <div>{links.length === 0 ? <small>{tx('ui.collections.workflow_unmapped')}</small> : links.map(w =>
      <span className="xo-collection-workflow" key={w.versionId} title={`${w.name} · ${w.analysis.name}`}>{w.code}
        <small>{tx('ui.lab.flow.version', { version: w.versionNo })}</small>
      </span>)}</div>
  </div>;
}

export function ContainerSymbol({ code }) {
  return <svg viewBox="0 0 28 44" aria-hidden="true" className="xo-collection-container-symbol">
    {code === 'stool_container'
      ? <><rect x="4" y="12" width="20" height="26" rx="4" /><path d="M3 12h22V7H3zM8 24h12M8 29h8" /></>
      : <><path d="M8 9v26a6 6 0 0 0 12 0V9M6 4h16v5H6z" /><path className="xo-collection-container-fill" d="M9 25h10v10a5 5 0 0 1-10 0z" /><path d="M12 15h4M12 20h4" /></>}
  </svg>;
}

function TemplateContents({ template }) {
  return <section className="xo-collection-contents" aria-label={tx('ui.collections.contents')}>
    <header><span>{tx('ui.collections.sample_type')}</span><strong>{ownSampleTypeName(template.sampleType)}</strong></header>
    {template.samples.map(line => <div className="xo-collection-content-line" key={line.id}>
      <div className="xo-collection-container-symbols">{Array.from({ length: Math.min(line.count, 5) }, (_, i) => <ContainerSymbol key={i} code={line.container.code} />)}</div>
      <span>{tx('ui.collections.container_quantity', { count: line.count, container: line.container.name })}</span>
    </div>)}
  </section>;
}

// The sample types a tube can be made of, as the picker lists them: each by
// the name staff read, in that order.
export function sampleTypeChoices(sampleTypes) {
  return sampleTypes.filter(type => type.ready).map(type => ({ ...type, label: ownSampleTypeName(type) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function downloadCodes(collections) {
  const cell = value => {
    const text = String(value);
    return `"${(/^[=+@\-\t\r]/.test(text) ? "'" + text : text).replace(/"/g, '""')}"`;
  };
  const csv = [
    [tx('ui.collections.code'), tx('ui.collections.sample_code'), tx('ui.collections.sample_type'), tx('ui.collections.contents'), tx('ui.collections.pipeline_choices')],
    ...collections.flatMap(c => c.samples.map(s => [c.printedCode ?? c.code, s.printedCode ?? s.code, ownSampleTypeName(c.sampleType), s.container.name, c.analyses.map(a => a.pipeline.displayName).join('; ')])),
  ].map(row => row.map(cell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url; link.download = collections.length === 1 ? `collection-${collections[0].printedCode ?? collections[0].code}.csv` : 'collection-sample-codes.csv'; link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function CollectionVisual({ collection }) {
  return <span className="xo-collection-visual" aria-hidden="true">
    {collection.product !== null && typeof collection.product.mainImageThumbnailUrl === 'string'
      ? <img src={collection.product.mainImageThumbnailUrl} alt="" loading="lazy" decoding="async" />
      : <span className="xo-collection-container-symbols">{collection.samples.slice(0, 3).map(sample => <ContainerSymbol key={sample.uuid} code={sample.container.code} />)}</span>}
  </span>;
}

function ProductAttachment({ collection }) {
  return <span className="xo-collection-attachment">
    {collection.product === null ? <><FlaskConical aria-hidden="true" />{tx('ui.collections.no_product')}</>
      : <><Package aria-hidden="true" /><span>{collection.product.name}</span></>}
  </span>;
}

// What a customer types in the app to activate the kit. Only a kit sold as a
// product has one.
function ActivationCode({ code }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard unavailable */ }
  }
  return <div className="xo-collection-activation-code">
    <small>{tx('ui.kits.activation_code_e0a37243')}</small>
    <code>{code}</code>
    <button type="button" className="xo-btn-2" onClick={copy}>
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}{copied ? tx('ui.collections.copied') : tx('ui.mobile.copy')}
    </button>
  </div>;
}

function CollectionPrintButton({ collection, onPrinted }) {
  const [open, setOpen] = useState(false);
  const [above, setAbove] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const root = useRef(null);
  const trigger = useRef(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    const resized = () => setOpen(false);
    document.addEventListener('mousedown', outside); window.addEventListener('resize', resized);
    return () => { document.removeEventListener('mousedown', outside); window.removeEventListener('resize', resized); };
  }, [open]);

  async function toggle() {
    if (open) { setOpen(false); return; }
    const boundary = root.current.closest('.xo-collection-result-list');
    const anchor = trigger.current.getBoundingClientRect(), width = Math.min(244, window.innerWidth - 32);
    const left = Math.max(16, Math.min(anchor.right - width, window.innerWidth - width - 16));
    setMenuStyle({ left: left - root.current.getBoundingClientRect().left, right: 'auto', width });
    setAbove(trigger.current.getBoundingClientRect().bottom + 220 > (boundary === null ? window.innerHeight - 16 : boundary.getBoundingClientRect().bottom));
    setOpen(true); setError(''); setDevices(null); setLoading(true);
    try { setDevices(await loadCollectionPrinters()); }
    catch (caught) { setError(errorMessage(caught)); }
    finally { setLoading(false); }
  }

  async function print(device) {
    if (inFlight.current) return;
    inFlight.current = true; setPrinting(true); setOpen(false); setError('');
    try { onPrinted(await printCollectionLabels(collection.uuid, device.deviceId)); }
    catch (caught) { setError(errorMessage(caught)); }
    finally { inFlight.current = false; setPrinting(false); trigger.current?.focus(); }
  }

  function keyDown(event) {
    if (event.key === 'Escape' && open) {
      event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.current.focus(); return;
    }
    if (!open || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const items = [...root.current.querySelectorAll('[role="menuitem"]')];
    if (!items.length) return;
    event.preventDefault(); event.stopPropagation();
    const current = items.indexOf(document.activeElement);
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
      : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[index].focus();
  }

  const isPrinted = collection.printedAt !== null;
  return <div className="xo-collection-print-control" ref={root} onKeyDown={keyDown}>
    <button type="button" className={`xo-btn-2${isPrinted ? ' xo-collection-print-confirmed' : ''}`} ref={trigger}
      disabled={printing || isPrinted || collection.activatedAt !== null} onClick={toggle} aria-haspopup="menu" aria-expanded={open}>
      {isPrinted ? <Check aria-hidden="true" /> : <Printer aria-hidden="true" />}
      {tx(isPrinted ? 'ui.collections.printed' : printing ? 'ui.collections.printing' : 'ui.collections.print')}
      {!isPrinted && <ChevronDown aria-hidden="true" />}
    </button>
    {open && <div className={`xo-collection-printer-menu${above ? ' is-above' : ''}`} style={menuStyle} role="menu" aria-label={tx('ui.collections.choose_printer')}>
      {loading ? <span role="status">{tx('ui.collections.loading_printers')}</span>
        : devices !== null && (devices.length === 0 ? <span>{tx('ui.collections.no_printers')}</span>
          : devices.map(device => <button type="button" role="menuitem" key={device.deviceId} onClick={() => print(device)}>
            <Printer aria-hidden="true" /><span><code>{device.deviceId}</code><small>{device.type.model} · {device.type.dpi} dpi</small>
              {!device.printConfigured && <small>{tx('ui.collections.printer_not_connected')}</small>}</span>
          </button>))}
    </div>}
    {error && <p className="xo-collection-print-error" role="alert">{error}</p>}
  </div>;
}

function CollectionResultRow({ collection, onPrinted }) {
  return <article className="xo-collection-result-row">
    <CollectionVisual collection={collection} />
    <div className="xo-collection-identity"><strong>{ownSampleTypeName(collection.sampleType)}</strong><ProductAttachment collection={collection} /></div>
    <div className="xo-collection-codes-right"><code className="xo-collection-parent-code">{collection.printedCode ?? collection.code}</code><SampleCodes samples={collection.samples} /></div>
    <div className="xo-collection-row-actions">
      <button type="button" className="xo-icon-btn" onClick={() => downloadCodes([collection])}
        aria-label={tx('ui.collections.download_collection', { code: collection.printedCode ?? collection.code })} title={tx('ui.collections.download_csv')}><Download aria-hidden="true" /></button>
      <CollectionPrintButton collection={collection} onPrinted={onPrinted} />
    </div>
  </article>;
}

// Who activated the collection, how, when and where, as recorded.
function ActivationSection({ activation, mayReadAccounts, now }) {
  const by = activation.by;
  const secondary = by === null ? text('activation.unrecorded_detail')
    : by.kind === 'staff' ? [by.email, by.location && text('activation.staff_at', { location: by.location.name })].filter(Boolean).join(' · ')
      : by.status === 'shown' ? (by.name ? by.email : null) : activatorNote(activation);
  return <section className="xo-collection-detail-section xo-collection-activation">
    <h3>{text('activation.title')}</h3>
    <div className="xo-collection-activator">
      <ActivatorAvatar activation={activation} />
      <span><strong>{activatorName(activation)}</strong>{secondary && <small>{secondary}</small>}</span>
      <span className="xo-collection-activator-facts">
        <span title={ago(activation.at, now)}>{when(activation.at)}</span>
        {activation.place !== null && <span className="xo-collection-place" title={activation.place.timezone || undefined}>
          {activation.place.countryCode && <Flag code={activation.place.countryCode} />}{placeLabel(activation.place)}</span>}
      </span>
      <ChannelMark channel={activation.channel} />
    </div>
    {mayReadAccounts && by !== null && by.kind === 'mobile_account' && by.status === 'shown' && <Link className="xo-mob-link"
      to={`/xomics/mobile/accounts?account=${encodeURIComponent(by.uuid)}`}>{text('activation.open_account')}<ChevronRight aria-hidden="true" /></Link>}
  </section>;
}

function stepDetail(step, collection) {
  const activation = collection.activation;
  if (step.step === 'activated' && activation !== null) {
    return [activatorName(activation), text(activation.channel === null ? 'activation.channel_none' : `activation.channel_${activation.channel}`),
      placeLabel(activation.place)].filter(Boolean).join(' · ');
  }
  const parts = [step.by && text('history.by', { name: step.by.name }), step.location && step.location.name,
    step.step === 'printed' && collection.printedDeviceId !== null && tx('ui.collections.device_id', { id: collection.printedDeviceId }),
    step.automatic && text('history.automatic')].filter(Boolean);
  return parts.length === 0 ? text('history.no_detail') : parts.join(' · ');
}

// Every step the collection has taken, as recorded, oldest first.
function CollectionSteps({ collection }) {
  return <section className="xo-collection-detail-section xo-collection-steps"><h3>{text('history.title')}</h3>
    <Timeline items={collection.history.map((step, index) => ({ id: `${step.step}-${index}`, tone: 'ok', title: stepLabel(step.step),
      when: when(step.at), detail: stepDetail(step, collection) }))} />
  </section>;
}

function CollectionDetailsModal({ initialCollection, mayPrint, mayTrack, mayReadAccounts, onClose, onChanged }) {
  const [collection, setCollection] = useState(initialCollection);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const now = useNow(30000);
  const dialog = useRef(null);
  const closing = useRef(onClose);
  closing.current = onClose;
  useEffect(() => {
    let active = true;
    loadCollection(initialCollection.uuid).then(value => { if (active) { setCollection(value); setLoaded(true); } })
      .catch(caught => { if (active) setError(errorMessage(caught)); });
    return () => { active = false; };
  }, [initialCollection.uuid]);
  useEffect(() => {
    const previousFocus = document.activeElement, previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; dialog.current.focus();
    const onKey = event => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') { event.preventDefault(); closing.current(); return; }
      if (event.key !== 'Tab') return;
      const controls = [...dialog.current.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]')].filter(element => element.getClientRects().length > 0);
      const first = controls[0], last = controls[controls.length - 1];
      if (document.activeElement === dialog.current || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; previousFocus?.focus(); };
  }, []);
  function printed(receipt) {
    setCollection(current => ({ ...current, printedAt: receipt.printedAt, printedDeviceId: receipt.deviceId })); onChanged();
  }
  return <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="xo-product-editor xo-collection-detail-modal" role="dialog" aria-modal="true" aria-labelledby="collection-detail-title" tabIndex={-1} ref={dialog}>
      <header><div><h2 id="collection-detail-title">{tx('ui.collections.detail_title', { code: collection.printedCode ?? collection.code })}</h2></div>
        <button type="button" className="xo-icon-btn" onClick={onClose} aria-label={tx('ui.collections.close')}><X aria-hidden="true" /></button></header>
      <div className="xo-collection-detail-body">
        {error !== '' ? <Failed message={error} /> : <>
          <div className="xo-collection-detail-identity"><CollectionVisual collection={collection} /><div className="xo-collection-identity">
            <CollectionStatus collection={collection} /><strong>{ownSampleTypeName(collection.sampleType)}</strong>
            <span className="xo-collection-identity-meta"><ProductAttachment collection={collection} />
              {collection.location !== null
                ? <span className="xo-collection-location"><MapPin aria-hidden="true" />{collection.location.name}</span>
                : <span className="xo-collection-location is-clinic"><Building2 aria-hidden="true" />{collection.clinic.name}</span>}</span></div>
          {collection.activationCode && <ActivationCode code={collection.activationCode} />}</div>
          {collection.activation !== null && <ActivationSection activation={collection.activation} mayReadAccounts={mayReadAccounts} now={now} />}
          {loaded ? <CollectionSteps collection={collection} /> : <Loading label={text('history.loading')} />}
          {/* One tube carries the collection's own code; only a kit of several lists them. */}
          {collection.samples.length > 1 && <section className="xo-collection-detail-section"><h3>{tx('ui.collections.samples')}</h3>
            <div className="xo-collection-detail-samples">{collection.samples.map(sample => <div key={sample.uuid}>
              <ContainerSymbol code={sample.container.code} /><span>{sample.container.name}</span><code>{sample.printedCode ?? sample.code}</code></div>)}</div>
          </section>}
          {collection.analyses.length > 0 && <section className="xo-collection-detail-section"><h3>{tx('ui.collections.pipeline_choices')}</h3>
            <div className="xo-collection-row-pipelines">{collection.analyses.map(analysis => <PipelineLabel key={analysis.uuid} pipeline={analysis.pipeline} />)}</div>
            <WorkflowLinks pipelines={collection.analyses.map(analysis => analysis.pipeline)} />
          </section>}
        </>}
      </div>
      <footer className="xo-collection-close-footer"><div className="xo-collection-row-actions">
        {mayTrack && collection.activatedAt !== null && error === '' && <Link className="xo-btn-2"
          to={`/xomics/sample-tracking?sampleUuid=${encodeURIComponent(collection.uuid)}`}>
          <MapPin aria-hidden="true" />{tx('ui.kits.open_sample_tracking')}</Link>}
        <button type="button" className="xo-btn-2" onClick={() => downloadCodes([collection])}><Download aria-hidden="true" />{tx('ui.collections.download_csv')}</button>
        {mayPrint && error === '' && <CollectionPrintButton collection={collection} onPrinted={printed} />}</div>
        <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.collections.close')}</button></footer>
    </section>
  </div>;
}

function GenerationModal({ catalogue, onGenerated, onClose }) {
  const [mode, setMode] = useState('product');
  const [productId, setProductId] = useState('');
  const [sampleCode, setSampleCode] = useState('');
  const [count, setCount] = useState('10');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const attempt = useRef(null);
  const submitting = useRef(false);
  const dialog = useRef(null);
  const closing = useRef(onClose);
  closing.current = onClose;
  const product = catalogue.products.find(p => p.id === Number(productId));
  const template = catalogue.templates.find(t => t.versionId === product?.templateVersionId);
  const sampleTypes = useMemo(() => sampleTypeChoices(catalogue.sampleTypes), [catalogue.sampleTypes]);
  const sampleType = sampleTypes.find(type => type.code === sampleCode);
  // A product brings its own pipeline. A tube of a sample type is made without
  // one: the laboratory picks its workflow and the upload its pipelines.
  const chosen = template === undefined ? [] : catalogue.pipelines.filter(p => template.pipelineIds.includes(p.id) && p.id === product?.pipelineId);
  const valid = Number.isSafeInteger(Number(count)) && Number(count) > 0 && Number(count) <= 500
    && (mode === 'product' ? template?.ready === true && chosen.length > 0 && product?.ready === true : sampleType !== undefined);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current.focus();
    const onKey = event => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') { event.preventDefault(); closing.current(); return; }
      if (event.key !== 'Tab') return;
      const controls = [...dialog.current.querySelectorAll('button:not([disabled]), input:not([disabled]), [tabindex="0"]')]
        .filter(element => element.getClientRects().length > 0);
      if (controls.length === 0) { event.preventDefault(); dialog.current.focus(); return; }
      const first = controls[0], last = controls[controls.length - 1];
      if (!dialog.current.contains(document.activeElement) || document.activeElement === dialog.current
          || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; previousFocus?.focus(); };
  }, []);

  async function submit(event) {
    event.preventDefault();
    if (!valid || submitting.current) return;
    submitting.current = true; setBusy(true); setError('');
    const input = mode === 'product'
      ? { mode, productId: Number(productId), count: Number(count) }
      : { mode: 'template', templateVersionId: sampleType.templateVersionId, count: Number(count) };
    const signature = JSON.stringify(input);
    if (attempt.current === null || attempt.current.signature !== signature) attempt.current = { signature, requestId: window.crypto.randomUUID() };
    try {
      const generated = await generateCollections({ ...input, requestId: attempt.current.requestId });
      setResult(generated); attempt.current = null; onGenerated();
    } catch (caught) { setError(errorMessage(caught)); }
    finally { submitting.current = false; setBusy(false); }
  }

  function printed(receipt) {
    setResult(current => ({ ...current, collections: current.collections.map(collection => collection.uuid === receipt.uuid
      ? { ...collection, printedAt: receipt.printedAt, printedDeviceId: receipt.deviceId } : collection) }));
    onGenerated();
  }

  return <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="xo-product-editor xo-collection-modal" role="dialog" aria-modal="true" aria-labelledby="collection-modal-title" tabIndex={-1} ref={dialog}>
      <header><div><h2 id="collection-modal-title">{result === null ? tx('ui.collections.new_title') : tx(result.count === 1 ? 'ui.collections.created_title_one' : 'ui.collections.created_title', { count: result.count })}</h2></div>
        <button type="button" className="xo-icon-btn" onClick={onClose} aria-label={tx('ui.collections.close')}><X aria-hidden="true" /></button>
      </header>
      {result !== null ? <>
        <div className="xo-collection-result-list" aria-live="polite">
          {result.collections.map(collection => <CollectionResultRow key={collection.uuid} collection={collection} onPrinted={printed} />)}
        </div>
        <footer className="xo-collection-close-footer"><button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.collections.close')}</button></footer>
      </> : busy ? <div className="xo-collection-generating" role="status"><Loading /><span>{tx('ui.collections.generating')}</span></div> : <div className="xo-collection-generator">
        <WorkspaceTabs value={mode} onChange={next => { if (!busy) { setMode(next); setResult(null); setError(''); } }} className="xo-product-sections xo-collection-mode-tabs"
          idPrefix="collection-mode" panelId="collection-generator-panel" label={tx('ui.collections.generate_by')}
          tabs={[
            { value: 'product', label: tx('ui.collections.by_product'), Icon: Package, count: catalogue.products.length },
            { value: 'sample_type', label: tx('ui.collections.by_sample_type'), Icon: FlaskConical, count: sampleTypes.length },
          ]} />
        <div id="collection-generator-panel" className="xo-collection-generator-panel" role="tabpanel" aria-labelledby={`collection-mode-${mode}`}>
          <form onSubmit={submit} className="xo-collection-generation-form">
            <div className="xo-collection-generator-scroll">
            <div className="xo-collection-generator-main">
              <div className="xo-collection-choice">
                <div className="xo-collection-section-heading"><h3>{tx(mode === 'product' ? 'ui.collections.product' : 'ui.collections.sample_type')}</h3></div>
                  <XomicsSelect id="collection-source" aria-label={tx(mode === 'product' ? 'ui.collections.product' : 'ui.collections.sample_type')}
                    className={mode === 'product' ? 'xo-collection-product-select' : 'xo-collection-type-select'} value={mode === 'product' ? productId : sampleCode} disabled={busy}
                    onChange={event => {
                      if (mode === 'product') setProductId(event.target.value);
                      else setSampleCode(event.target.value);
                      setError(''); setResult(null);
                    }}>
                    <option value="">{tx(mode === 'product' ? 'ui.collections.choose_product' : 'ui.collections.choose_sample_type')}</option>
                    {mode === 'product' ? catalogue.products.map(item => <option key={item.code} value={item.id}>
                      <span className="xo-collection-product-option"><span className="xo-collection-product-thumbnail">
                        {typeof item.mainImageThumbnailUrl === 'string' && <img src={item.mainImageThumbnailUrl} alt="" width="64" height="48" decoding="async" />}
                      </span><span>{item.name}</span></span></option>)
                      : sampleTypes.map(type => <option key={type.code} value={type.code}>
                        <span className="xo-collection-type-option"><span className="xo-collection-type-glyph"><SpecimenGlyph code={type.labCode} /></span>
                          <span><strong>{type.label}</strong>
                            <small>{type.workflows.length === 0 ? text('tubes.no_workflow') : type.workflows.map(workflow => workflow.code).join(' · ')}</small></span>
                        </span></option>)}
                  </XomicsSelect>
                <p className="xo-collection-hint">{text(mode === 'product' ? 'tubes.product_hint' : 'tubes.one_per_code')}</p>
                {mode === 'product' && template !== undefined && <TemplateContents template={template} />}
              </div>
              {mode === 'product' && <div className="xo-collection-resolved">
                <div className="xo-collection-section-heading"><h3>{tx('ui.collections.pipeline_choices')}</h3><b>{chosen.length}</b></div>
                {template === undefined ? <div className="xo-collection-choice-empty"><ScanLine aria-hidden="true" />
                  <span>{text(product !== undefined ? 'tubes.product_missing' : 'tubes.product_preview')}</span></div>
                  : <>
                    {chosen.length === 0 ? <p className="xo-collection-hint" role="status">{tx('ui.collections.no_pipelines')}</p>
                      : <div className="xo-collection-pipeline-options">{chosen.map(pipeline =>
                        <div className="xo-collection-pipeline-option is-fixed" key={pipeline.id}><Check aria-hidden="true" /><PipelineLabel pipeline={pipeline} /></div>)}</div>}
                    <WorkflowLinks pipelines={chosen} />
                  </>}
              </div>}
              {mode === 'sample_type' && <div className="xo-collection-resolved">
                <div className="xo-collection-section-heading"><h3>{text('tubes.workflows')}</h3>{sampleType !== undefined && <b>{sampleType.workflows.length}</b>}</div>
                {sampleType === undefined ? <div className="xo-collection-choice-empty"><Workflow aria-hidden="true" /><span>{text('tubes.workflows_preview')}</span></div>
                  : sampleType.workflows.length === 0 ? <p className="xo-collection-hint" role="status">{tx('ui.lab.assign.no_workflow')}</p>
                    : <>
                      <ul className="xo-collection-type-workflows">{sampleType.workflows.map(workflow => <li key={workflow.code}>
                        <b>{workflow.code}</b><span><strong>{workflowName(workflow.code)}</strong><small>{departmentShort(workflow.departmentCode)}</small></span>
                      </li>)}</ul>
                      <p className="xo-collection-hint">{text('tubes.workflows_hint')}</p>
                    </>}
              </div>}
            </div>

          {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
            </div>
            <footer className="xo-collection-generator-footer">
              <Field id="collection-count" label={mode === 'product' ? tx('ui.collections.quantity') : text('tubes.quantity')}>
                <input {...fieldAria('collection-count')} type="number" min="1" max="500" step="1" value={count}
                  disabled={busy} onChange={event => setCount(event.target.value)} required />
              </Field>
              <button type="submit" className="xo-btn xo-product-new-button" disabled={busy || !valid}><Plus aria-hidden="true" />{tx(busy ? 'ui.collections.generating' : 'ui.collections.generate')}</button>
            </footer>
          </form>
        </div>
      </div>}
    </section>
  </div>;
}

// The tubes of a kit of several, each with its own code. One tube carries the
// collection's code, which is already beside it.
function SampleCodes({ samples }) {
  if (samples.length < 2) return null;
  return <span className="xo-collection-sample-codes" aria-label={tx('ui.collections.samples')}>
    {samples.map(sample => <code key={sample.uuid} title={sample.container.name}>{sample.printedCode ?? sample.code}</code>)}
  </span>;
}

function collectionState(collection) {
  return collection.activatedAt !== null ? 'activated' : collection.printedAt !== null ? 'printed' : 'stock';
}

function CollectionStatus({ collection }) {
  const state = collectionState(collection);
  return <Pill tone={state === 'activated' ? 'ok' : state === 'printed' ? 'info' : 'flat'}>{tx('ui.collections.' + state)}</Pill>;
}

// What happened to a collection last, for its row: who activated it and how, or
// that it is still waiting and since when.
function RowActivity({ collection, now }) {
  const activation = collection.activation;
  if (activation !== null) {
    return <span className="xo-collection-activity">
      <ActivatorAvatar activation={activation} />
      <span className="xo-collection-activity-main">
        <strong>{activatorName(activation)}</strong>
        <small><ChannelMark channel={activation.channel} /><span title={when(activation.at)}>{ago(activation.at, now)}</span>
          <ShortPlace place={activation.place} /></small>
      </span>
    </span>;
  }
  const printed = collection.printedAt !== null;
  const at = printed ? collection.printedAt : collection.createdAt;
  return <span className="xo-collection-activity is-waiting">
    <span className="xo-avatar xo-collection-avatar-icon" aria-hidden="true">{printed ? <Printer /> : <Package />}</span>
    <span className="xo-collection-activity-main">
      <strong>{text('row.not_activated')}</strong>
      <small title={when(at)}>{text(printed ? 'row.printed_ago' : 'row.created_ago', { when: ago(at, now) })}</small>
    </span>
  </span>;
}

function InventoryItem({ collection, view, scope, now, selection, selected, onOpen }) {
  return <article className={`xo-collection-inventory-item${selection ? ' has-selection' : ''}${selected ? ' is-selected' : ''}`}>
    {selection && <div className="xo-collection-selection">{selection}</div>}
    <button type="button" className="xo-collection-open" onClick={() => onOpen(collection)}
      aria-label={tx('ui.collections.open_collection', { code: collection.printedCode ?? collection.code })}>
      <CollectionVisual collection={collection} />
      <span className="xo-collection-identity">
        <code className="xo-collection-parent-code">{collection.printedCode ?? collection.code}</code>
        <strong>{ownSampleTypeName(collection.sampleType)}</strong>
        <span className="xo-collection-identity-meta"><ProductAttachment collection={collection} />
          {scope === 'global' && (collection.location !== null
            ? <span className="xo-collection-location"><MapPin aria-hidden="true" />{collection.location.code}</span>
            : <span className="xo-collection-location is-clinic"><Building2 aria-hidden="true" />{collection.clinic.name}</span>)}</span>
      </span>
      <RowActivity collection={collection} now={now} />
      <span className="xo-collection-row-state"><CollectionStatus collection={collection} />
        {(collection.samples.length > 1 || collection.analyses.length > 0) && <small>
          {[collection.samples.length > 1 ? counted('ui.collections.sample_count', collection.samples.length) : null,
            collection.analyses.length === 0 ? null : tx(collection.analyses.length === 1 ? 'ui.collections.pipeline_count_one' : 'ui.collections.pipeline_count',
              { count: collection.analyses.length })].filter(Boolean).join(' · ')}</small>}
      </span>
      {view === 'grid' && <SampleCodes samples={collection.samples} />}
      <ChevronRight className="xo-collection-open-chevron" aria-hidden="true" />
    </button>
  </article>;
}

const SECTIONS = ['overview', 'inventory'];

// A viewer's own choices, remembered in this browser only.
function remembered(key, allowed, fallback) {
  try { const value = window.localStorage.getItem(key); return allowed.includes(value) ? value : fallback; }
  catch (_error) { return fallback; }
}

function remember(key, value) {
  try { window.localStorage.setItem(key, value); } catch (_error) { /* The choice still holds for this visit. */ }
}

export function CollectionWorkspace({ mayManage, mayReadGlobal, mayTrack, mayTrackGlobal, mayReadAccounts, location }) {
  const [section, setSection] = useState(() => remembered('xo-collections-section', SECTIONS, 'overview'));
  const [scope, setScope] = useState('local');
  const [days, setDays] = useState(30);
  const [state, setState] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState(() => remembered('xo-collections-view', ['grid', 'list'], 'list'));
  const now = useNow(30000);
  const loadCatalogue = useCallback(() => loadCollectionCatalogue(), []);
  const options = useLiveResource(['products', 'pipelines'], loadCatalogue);
  const load = useCallback(() => loadCollections({ scope, state, q: query, offset, limit: 50 }), [scope, state, query, offset]);
  const inventory = useLiveResource(['collections'], load, { enabled: section === 'inventory' });
  const closeGenerator = useCallback(() => setAdding(false), []);
  useEffect(() => { const timer = window.setTimeout(() => { setQuery(search.trim()); setOffset(0); }, 200); return () => window.clearTimeout(timer); }, [search]);
  useEffect(() => { setSelected([]); }, [scope, state, query, offset]);
  useEffect(() => { if (!mayReadGlobal) setScope('local'); }, [mayReadGlobal]);
  const data = inventory.data;
  const currentSelection = useMemo(() => data === null ? [] : data.collections.filter(c => selected.includes(c.uuid)), [data, selected]);
  const countFor = value => data === null ? undefined : data.summary[value === 'all' ? 'total' : value];
  function changeSection(next) { setSection(next); remember('xo-collections-section', next); }
  function changeView(next) { setView(next); remember('xo-collections-view', next); }
  function showActivated() { setState('activated'); setOffset(0); changeSection('inventory'); }
  const selectionControl = collection => mayManage && <input type="checkbox" aria-label={tx('ui.collections.select_code', { code: collection.printedCode ?? collection.code })}
    checked={selected.includes(collection.uuid)}
    onChange={event => setSelected(ids => event.target.checked ? [...ids, collection.uuid] : ids.filter(id => id !== collection.uuid))} />;
  const allControl = mayManage && <input type="checkbox" aria-label={tx('ui.collections.select_page')} disabled={data === null || data.collections.length === 0}
    checked={data !== null && data.collections.length > 0 && data.collections.every(c => selected.includes(c.uuid))}
    onChange={event => setSelected(event.target.checked ? data.collections.map(c => c.uuid) : [])} />;

  return <div className="xo-products-page xo-collections">
    <Head title={tx('ui.collections.title')} subtitle={text('page_subtitle')}>
      {mayManage && <button type="button" className="xo-btn xo-product-new-button" disabled={options.data === null || Boolean(options.error)} onClick={() => setAdding(true)}><Plus aria-hidden="true" />{tx('ui.collections.new')}</button>}
    </Head>
    <div className="xo-product-navigation">
      <div className="xo-product-section-row">
        <WorkspaceTabs className="xo-product-sections xo-collection-sections" value={section} onChange={changeSection} label={text('sections')}
          idPrefix="collections-section" panelId="collections-section-panel"
          tabs={[{ value: 'overview', label: text('section.overview'), Icon: BarChart3 }, { value: 'inventory', label: text('section.inventory'), Icon: Boxes }]} />
        <div className="xo-product-toolbar-actions">
          {section === 'overview' && <RangeTabs value={days} onChange={setDays} />}
          {mayReadGlobal && <FilterMenu icon={MapPin} label={tx('ui.collections.scope')} value={scope} onChange={value => { setScope(value); setOffset(0); }}
            options={['local', 'global'].map(value => ({ value, label: text(`scope_${value}`),
              count: section === 'inventory' && data !== null ? data.scopeCounts[value] : undefined }))} />}
        </div>
      </div>
      {section === 'inventory' && <div className="xo-product-toolbar">
        <SearchField className="xo-product-search" value={search} onChange={setSearch} placeholder={tx('ui.collections.search')} />
        <div className="xo-product-toolbar-actions">
          <FilterMenu icon={SlidersHorizontal} label={tx('ui.collections.state')} value={state} onChange={value => { setState(value); setOffset(0); }}
            options={['all', 'stock', 'printed', 'activated'].map(value => ({ value, label: tx('ui.collections.' + value), count: countFor(value) }))} />
          <div className="xo-product-view-switch" role="group" aria-label={tx('ui.collections.layout')}>
            <button type="button" className={view === 'grid' ? 'is-active' : ''} aria-pressed={view === 'grid'} onClick={() => changeView('grid')} aria-label={tx('ui.products.grid_701c483f')}><LayoutGrid aria-hidden="true" /><span>{tx('ui.products.grid_701c483f')}</span></button>
            <button type="button" className={view === 'list' ? 'is-active' : ''} aria-pressed={view === 'list'} onClick={() => changeView('list')} aria-label={tx('ui.products.list_a1fffaaa')}><List aria-hidden="true" /><span>{tx('ui.products.list_a1fffaaa')}</span></button>
          </div>
        </div>
      </div>}
    </div>
    {options.error && mayManage && <Failed message={options.error} onRetry={options.refresh} />}
    <section id="collections-section-panel" className="xo-collection-section" role="tabpanel" aria-labelledby={`collections-section-${section}`}>
      {section === 'overview' ? <CollectionOverview scope={scope} days={days} now={now} onOpen={setViewing} onShowActivated={showActivated} /> : <>
        {inventory.loading && data === null && <Loading />}
        {inventory.error && <Failed message={inventory.error} onRetry={inventory.refresh} />}
        {data !== null && !inventory.error && <>
          <div className="xo-product-results-heading"><span className="xo-collection-results-count">{allControl}{tx('ui.collections.inventory')} <b>{data.total}</b></span>
            {currentSelection.length > 0 && <button type="button" className="xo-btn-2" onClick={() => downloadCodes(currentSelection)}><Download aria-hidden="true" />{tx('ui.collections.download_selected', { count: currentSelection.length })}</button>}
          </div>
          {data.collections.length === 0 ? <div className="xo-product-empty"><Empty title={tx(query === '' && state === 'all' ? 'ui.collections.empty' : 'ui.collections.no_matches')}
            detail={query !== '' ? tx('ui.collections.search_hint') : state === 'all' && mayManage ? tx('ui.collections.empty_start') : undefined} /></div>
            : <div className={`xo-collection-inventory is-${view}`}>
              {data.collections.map(collection => <InventoryItem key={collection.uuid} collection={collection} view={view} scope={scope} now={now}
                selection={selectionControl(collection)} selected={selected.includes(collection.uuid)} onOpen={setViewing} />)}
            </div>}
          {data.total > 0 && <footer className="xo-collection-pagination"><span>{tx('ui.collections.page_range', { from: offset + 1, to: offset + data.collections.length, total: data.total })}</span><div>
            <button type="button" className="xo-btn-2" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>{tx('ui.collections.previous')}</button>
            <button type="button" className="xo-btn-2" disabled={offset + data.collections.length >= data.total} onClick={() => setOffset(offset + 50)}>{tx('ui.collections.next')}</button>
          </div></footer>}
        </>}
      </>}
    </section>
    {viewing !== null && <CollectionDetailsModal key={viewing.uuid} initialCollection={viewing} mayPrint={mayManage && viewing.location?.id === location.id}
      mayTrack={mayTrack && (mayTrackGlobal || viewing.location?.id === location.id)} mayReadAccounts={mayReadAccounts}
      onClose={() => setViewing(null)} onChanged={inventory.refresh} />}
    {adding && options.data !== null && <GenerationModal catalogue={options.data} onClose={closeGenerator} onGenerated={() => { setOffset(0); inventory.refresh(); }} />}
  </div>;
}

export default function XomicsCollections() {
  const { permissions, user } = useXomicsAuth();
  return <CollectionWorkspace mayManage={permissions.includes('KIT_MANAGE')} mayReadGlobal={permissions.includes('KIT_READ_ALL')}
    mayTrack={permissions.includes('SAMPLE_TRACK_READ')} mayTrackGlobal={permissions.includes('SAMPLE_TRACK_READ_ALL')}
    mayReadAccounts={permissions.includes('PLATFORM_MOBILE_READ')} location={user.location} />;
}
